import React, { forwardRef } from "react";

import { useApi } from "@mb-pro-ui/utils";
import { Close, Edit, Phone } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slide,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface CallbackRequestProps {
  jobId: string;
}

export default function CallbackRequest({ jobId }: CallbackRequestProps) {
  const theme = useTheme();
  const api = useApi();
  const { formatMessage } = useIntl();

  const COUNTDOWN_SECONDS = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [comment, setComment] = useState("");
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [hasComment, setHasComment] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const { mutate: requestCallback, isPending } = useMutation({
    mutationFn: (commentText?: string) =>
      api(`/actions/raptor/jobs/${jobId}/request-callback`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: commentText || undefined,
      }),
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: formatMessage({
          defaultMessage: "Request sent!",
        }),
        severity: "success",
      });
      handleDialogToggle(false);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: formatMessage({
          defaultMessage: "An error occurred while sending the request.",
        }),
        severity: "success",
      });
    },
  });

  const handleDialogToggle = (isOpen: boolean) => {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (isOpen && themeColorMeta) {
      setDialogOpen(true);
      themeColorMeta.setAttribute("content", "#7e7e7e");
    } else {
      setDialogOpen(false);
      themeColorMeta?.setAttribute("content", "#fcfcfc");
      setIsCountdownActive(false);
      setCountdown(COUNTDOWN_SECONDS);
      setComment("");
      setHasComment(false);
    }
  };

  useEffect(() => {
    if (dialogOpen) {
      setCountdown(COUNTDOWN_SECONDS);
      setComment("");
      setHasComment(false);
      setIsCountdownActive(true);
    }
  }, [dialogOpen]);

  useEffect(() => {
    if (!dialogOpen || !isCountdownActive || hasComment) return;

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      requestCallback(undefined);
    }
  }, [countdown, dialogOpen, isCountdownActive, hasComment, requestCallback]);

  const handleCommentFocus = () => {
    if (!hasComment) {
      setHasComment(true);
      setIsCountdownActive(false);
    }
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setComment(value);

    if (!hasComment) {
      setHasComment(true);
      setIsCountdownActive(false);
    }
  };

  const handleSendNow = () => {
    requestCallback(comment.trim() || undefined);
  };

  return (
    <>
      <Button
        variant={"contained"}
        color={"error"}
        onClick={() => handleDialogToggle(true)}
        fullWidth
        startIcon={<Phone />}
      >
        {formatMessage({ defaultMessage: "Request callback" })}
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={() => handleDialogToggle(false)}
        maxWidth="sm"
        fullWidth
        slots={{ transition: Transition }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
            px: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="raptor-main-h">
            {formatMessage({ defaultMessage: "Request callback" })}
          </Typography>

          <IconButton
            onClick={() => handleDialogToggle(false)}
            sx={{
              color: "text.primary",
              backgroundColor: "action.hover",
              "&:hover": {
                backgroundColor: "action.selected",
              },
            }}
            aria-label={formatMessage({
              defaultMessage: "Close",
            })}
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            px: 3,
            py: 3,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {isCountdownActive && !hasComment && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <Typography variant="h4" color="white">
                    {countdown}
                  </Typography>
                  <CircularProgress
                    size={64}
                    thickness={1.5}
                    color="warning"
                    sx={{
                      position: "absolute",
                      color: "error",
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    color="primary.main"
                    fontWeight={600}
                  >
                    {formatMessage({ defaultMessage: "Automatic sending" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMessage(
                      {
                        defaultMessage:
                          "We will send the request in {countdown} seconds",
                      },
                      { countdown },
                    )}
                  </Typography>
                </Box>
              </Box>
            )}

            {hasComment && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: `2px solid ${theme.palette.warning.main}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: "warning.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Edit sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    color="warning.main"
                    fontWeight={600}
                  >
                    {formatMessage({ defaultMessage: "Comment mode" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMessage({
                      defaultMessage: "Automatic sending stopped",
                    })}
                  </Typography>
                </Box>
              </Box>
            )}

            <TextField
              label={formatMessage({ defaultMessage: "Add a comment" })}
              multiline
              rows={3}
              value={comment}
              onChange={handleCommentChange}
              onFocus={handleCommentFocus}
              disabled={isPending}
              placeholder={formatMessage({
                defaultMessage: "Write a comment here if necessary...",
              })}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  "& fieldset": {
                    borderWidth: 2,
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: 2,
                  },
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            gap: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            onClick={() => handleDialogToggle(false)}
            disabled={isPending}
            variant="outlined"
            fullWidth
          >
            {formatMessage({ defaultMessage: "Cancel" })}
          </Button>
          <Button
            onClick={handleSendNow}
            disabled={isPending}
            variant="contained"
            fullWidth
          >
            <Typography variant="raptor-button-text">
              {formatMessage({ defaultMessage: "Send now" })}
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        slots={{ transition: Slide }}
      >
        <Alert
          severity={snackbar.severity as "success" | "error"}
          sx={{
            borderRadius: 3,
            "& .MuiAlert-icon": {
              fontSize: "1.2rem",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
